import { useAuth } from "context/AuthContext";
import React, { useEffect, useState } from "react";
import { getOrCreateConversationIssue } from "services/firestore/getOrCreateConversationIssue";
import styled from "styled-components";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { ClassInfoCard } from "ui/base/class/components/ClassInfoCard";
import { Modal } from "ui/base/modal/Modal";
import { TextArea } from "ui/base/textArea/TextArea";
import { AlertCircle } from "ui/icons/AlertCircle";
import { defaultThemeColors } from "ui/theme";
import { updateTypingStatus } from "../../../../services/database/updateTypingStatus";
import { sendMessage } from "../../../../services/firestore/sendMessage";

export const ReportModal = ({ classDetail, isVisible, onClose }) => {
  const { userData, userRole } = useAuth();

  const [text, setText] = useState("");
  const [conversationId, setConversationId] = useState(null);

  const [isMsgSent, setIsMsgSent] = useState(false);

  const creatorId = userData?.id?.toString();
  const participantId = "0";
  const classId = classDetail?.id?.toString();

  useEffect(() => {
    if (!classDetail) return;

    const fetchConversation = async () => {
      const id = await getOrCreateConversationIssue(
        creatorId,
        participantId,
        classId,
        userRole
      );

      setConversationId(id);
    };

    fetchConversation();
  }, [classDetail]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;

    try {
      if (!participantId) {
        console.error("Receiver ID not found");
        return;
      }

      setText("");

      updateTypingStatus(conversationId, creatorId, false);

      await sendMessage(
        text,
        conversationId,
        creatorId,
        participantId,
        "Teache Team"
      );

      await sendMessage(
        "We will review your class issue and get back to you shortly.",
        conversationId,
        participantId,
        creatorId,
        "Teache Team",
        false
      );

      setIsMsgSent(true);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <>
      <Modal
        show={isVisible}
        handleClose={() => onClose()}
        maxWidth={isMsgSent ? 500 : 900}
      >
        <ModalBody>
          {!isMsgSent ? (
            <>
              <LeftPart>
                <ClassInfoCard
                  classInfo={{
                    first_name: `${classDetail?.first_name} ${classDetail.last_name}`,
                    profile_pic: classDetail?.profile_pic,
                    class: classDetail?.class,
                    rating: classDetail?.rating,
                    class_type: classDetail?.class_type,
                    review: classDetail?.review,
                  }}
                />
              </LeftPart>
              <RightPart>
                <div>
                  <TextArea
                    label="Please describe the issue you experience with your class. A Teache representative will be in touch"
                    placeholder="Type your reason..."
                    style={{ height: 200 }}
                    value={text}
                    setValue={(text) => {
                      setText(text);
                    }}
                  />
                </div>
                <div style={{ width: "fit-content" }}>
                  <ButtonPrimary disabled={!text} onClick={handleSendMessage}>
                    Send Message
                  </ButtonPrimary>
                </div>
              </RightPart>
            </>
          ) : (
            <ThanksContent>
              <AlertWrap>
                <AlertCircle />
              </AlertWrap>
              <Title>Issue Reported</Title>
              <Message>
                A Teache representative will review the issue reported and get back to you.
              </Message>
              <FullButtonPrimary onClick={() => onClose()}>
                Done
              </FullButtonPrimary>
            </ThanksContent>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

const ModalBody = styled.div`
  display: flex;
  padding-inline: 1.5rem;
  gap: 2rem;
`;

const LeftPart = styled.div`
  width: 40%;

  & > img {
    object-fit: cover;
    max-width: 280px;
    width: 100%;
    aspect-ratio: 28 / 17;
    border-radius: 5px;
    border-width: 1px;
    border-style: solid;
    border-color: ${defaultThemeColors.lightBlue};
  }
`;

const RightPart = styled.div`
  width: 60%;
  display: grid;
  gap: 1rem;
`;

const ThanksContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
`;

const AlertWrap = styled.div`
  margin-bottom: 12px;
  padding: 20px;
  width: 45px;
  height: 45px;
  box-sizing: content-box;
  border-radius: 50%;
  background-color: #edf0ff;
`;

const Title = styled.h2`
  font-weight: 600;
  font-size: 26px;
  letter-spacing: 0%;
  text-align: center;
`;

const Message = styled.p`
  font-weight: 400;
  font-size: 18px;
  line-height: 155%;
  letter-spacing: 0%;
  text-align: center;
`;

const FullButtonPrimary = styled(ButtonPrimary)`
  width: calc(100% - 60px);
  margin-top: 20px;
`;
